<template>
  <form class="cso-login-with-password"
        @submit.prevent="login">
    <fieldset>
      <legend>{{ $t(header) }}</legend>
      <div v-if="termsAndConditionsOutdated">
        <cso-login-terms-and-conditions
          :error-text="tacError"
          @termsAndConditionsAccepted="termsAndConditionsAccepted"
          @termsAndConditionsDenied="termsAndConditionsDenied" />
      </div>
      <div class="hidden-print">
        <h3>{{ $t(header) }}</h3>
        <h4 class="normal-text">
          {{ $t('login.title.instructionToLoginWithPassword') }}
        </h4>
        <cso-input name="loginModel.username"
                   :label="$t('login.labels.username')"
                   class="custom-margin"
                   ref="inputUsername"
                   cypressName="login-input"
                   maxlength="200"
                   validationName="login.validations.username"
                   :validation="v$.username"
                   v-model="username">
        </cso-input>
        <cso-input type="password"
                   name="loginModel.password"
                   :label="$t('login.labels.password')"
                   :hint="$t('login.tooltips.password')"
                   cypressName="password-input"
                   maxlength="35"
                   validationName="login.validations.password"
                   :validation="v$.password"
                   v-model="password">
        </cso-input>
        <div class="buttons">
          <cso-button 
                      type="submit"
                      class="custom-width"
                      cypressName="login-btn"
                      :label="$t('login.buttons.login')">
          </cso-button>
          <cso-link :link="StaticUrls.forgottenPassword"
                    class="link-password-forgotten"
                    cypressName="forgot-pass-link"
                    :label="$t('login.buttons.forgottenPassword')" />
        </div>
        <div class="footer">
          <h4 class="paragraph">{{ $t('login.registration') }}</h4>
          <cso-link :link="StaticUrls.register"
                    cypressName="registration-link"
                    :label="$t('login.buttons.register')" />
        </div>
      </div>
    </fieldset>
  </form>
</template>

<script lang="ts">
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'

import di from '@/api/digital-interface'
import * as DI from '@/api/digital-interface/client'
import Utils from '@/common/Utils'
import StaticUrls from '@/common/StaticUrls'
import LoggingService from '@/common/LoggingService'
import Authentication from '@/common/Authentication'

import CsoInput from '@/components/core/form/CsoInput.vue'
import CsoLoginTermsAndConditions from './CsoLoginTermsAndConditions.vue'

export default {
  name: 'CsoLoginWithPassword',
  components: {
    CsoInput,
    CsoLoginTermsAndConditions
  },
  props: {
    header: {
      type: String,
      required: false,
      default: 'login.title.justPassword'
    },
    showRegisterButton: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      url: 'https://www.vattenfall.de/agb',
      error: '',
      StaticUrls,
      checkOnlyUserId: false,
      termsAndConditionsOutdated: false,
      termsAndConditionsConfirmed: false,
      tacError: '',
      username: '',
      password: ''
    }
  },

  validations () {
    return {
      username: {
        required
      },
      password: {
        required
      }
    }
  },

  methods: {
    async login () {
      this.v$.$touch()
      if (this.v$.$invalid) {
        return
      }
      LoggingService.log(LoggingService.eventTypes.authentication, 'Login attempt with password')
      Utils.windowLoading(true)
      this.error = ''
      this.username = Utils.killSpace(this.username)
      try {
        this.termsAndConditionsOutdated = false
        await Authentication.login({
          username: this.username,
          password: this.password,
          twoStepAuth: true,
          checkOnlyUserId: this.checkOnlyUserId,
          termsAndConditionsConfirmed: this.termsAndConditionsConfirmed
        })
      } catch (error) {
        LoggingService.error(LoggingService.eventTypes.authentication, 'Login failed', error)
        if (error instanceof DI.ErrorHttp && error.status === 403) {
          this.error = this.$t('general.ipBlocked') as string
        } else if (error instanceof DI.ErrorResponse) {
          if (error.code === 'ERROR_USER_USERNAME_NEEDED') {
            this.checkOnlyUserId = true
            this.error = error.text
          } else if (error.code === 'ERROR_NUTZUNGSBEDINGUNGEN_BESTAETIGUNG_ERFORDERLICH') {
            this.termsAndConditionsOutdated = true
            this.tacError = error.text
          } else {
            this.checkOnlyUserId = false
            this.termsAndConditionsConfirmed = false
            this.termsAndConditionsOutdated = false
            this.username = ''
            this.password = ''
            this.error = error.text
            this.v$.$reset()
          }
        } else {
          throw error
        }
      }
      this.$emit('server-error', this.error)
      Utils.windowLoading(false)
    },

    async deleteAccount () {
      Utils.windowLoading(true)
      try {
        await di.v13.deleteAccount({
          username: this.username,
          password: this.password
        })
        this.$router.push({ query: { accountDeleted: 'true' } })
      } catch (error) {
        const message = (error as any)?.message || this.$t('general.errors.post')
        this.$emit('server-error', message)
      }
      Utils.windowLoading(false)
    },

    termsAndConditionsAccepted () {
      this.termsAndConditionsConfirmed = true
      this.login()
    },

    termsAndConditionsDenied () {
      this.termsAndConditionsOutdated = false
      this.deleteAccount()
    },

    goToFaq () {
      let element = document.querySelector('.cso-faq')
      if (element && element.scrollIntoView) {
        setTimeout(function () {
          element?.scrollIntoView({
            behavior: 'smooth'
          })
        }, 0)
      }
    }

  },

  setup () {
    return {
      v$: useVuelidate()
    }
  }
}
</script>

<style lang="scss">
.cso-login-with-password {

  .custom-margin {
    margin: 28px 0 24px 0;

    @include md-1 {
      margin-top: 16px;
    }

  }

  h3 {
    text-align: left;
    font-size: 24px;
    font-family: $font-family-bold;
    font-weight: normal;
    margin: 0 0 16px 0;

    @include md {
      font-size: 22px;
    }
  }

  h4 {
    font-size: 16px;
    font-weight: normal;

    @include xs {
        font-size: 14px;
    }
  }

  .buttons {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 28px 0;
    font-size: 16px;

    @include sm {
      flex-direction: column;
      align-items: flex-start;
      margin: 0;
    }

    button.custom-width {
      @include sm {
        width: 100%;
      }
    }

    .link-password-forgotten {

      @include sm {
        margin: 16px 0;
      }
    }
  }

  .footer {
    display: flex;
    justify-content: left;
    margin-top: 12px;
    font-size: 16px;

    .paragraph {
      margin-right: 5px;
    }

    @include sm {
      flex-direction: column;
      margin: 0;
    }
  }

  .terms-and-conditions {
    padding: 10px 0 15px 0;
  }

  fieldset {
    border-right: 1px solid $gray-light;
    padding: 0 44px 0 34px;

    @include md-1 {
      border-right: 0;
      border-bottom: 1px solid $gray-light;
      padding: 24px 6px;
    }
  }
}
</style>
